import { client, xml } from '@xmpp/client'

import Observable from '../helpers/observerable'
import APIManager from './apiManager'
import {
    ProtoBuf,
    CameraSettings,
    CameraInfo,
    CameraStatus,
    LiveControl,
} from 'alfred-protobuf'
import { isInstanceOf } from '@sentry/utils'

const RpcServiceList = {
    0: undefined, // SERVICE_RESERVED
    1: new CameraInfo.CameraInfoService(),
    2: new CameraSettings.CameraSettingsService(),
    3: new CameraStatus.CameraStatusService(),
    4: undefined, // RTC_SIGNALLING
    5: new LiveControl.LiveControlService(),
    6: undefined, // DEVICE_MANAGEMENT
    7: undefined, // LIVE_PREVIEW
    8: undefined, // MEDIA_ACCESS
}

const PING_SERVER_INTERVAL = 15000

export default class XMPPManager {
    instance = null
    constructor(props) {
        if (!this.instance) {
            this.instance = new XMPPManagerImplement(props)
        }
        return this.instance
    }
}

const retryIntervals = [5000, 25000, 125000]

const invalidStatusList = ['closing', 'close', 'disconnecting', 'disconnect']

class XMPPManagerImplement {
    deviceId = ''
    xmppJid = ''
    observable = ''
    pingServerInterval = null
    host = ''
    xmpp = null

    constructor({ kvToken, deviceId, uniqueId, xmppInfo, isFree }) {
        let host = 'tw-xmpp.my-alfred.com'
        if (xmppInfo) {
            host = xmppInfo.addr
        }
        this.username = `${uniqueId}@${host}`
        this.service = `wss://${host}/websocket`
        this.deviceId = deviceId
        this.xmppJid = `${this.username}/${deviceId}`
        this.observable = new Observable()
        this.host = host
        this.kvToken = kvToken

        this.initXMPP()
    }

    initXMPP() {
        this.xmpp = client({
            service: this.service,
            resource: this.deviceId,
            password: this.kvToken,
            username: this.username,
        })

        const send = this.xmpp.send.bind(this.xmpp)
        this.xmpp.send = function () {
            if (invalidStatusList.includes(this.xmpp.status)) {
                return
            }
            return send(...arguments)
        }
        this.xmpp.send = this.xmpp.send.bind(this)
        this.xmpp.on('stanza', this.stanza)
        this.xmpp.on('online', this.online)
        this.xmpp.on('offline', this.offline)
        this.xmpp.on('status', this.status)
        this.xmpp.on('error', this.onError)
        this.xmpp.reconnect.delay = retryIntervals[0]
        this.times = 0
        this.initIqCallee()
    }

    online = async (address) => {
        const arpcPresence = xml(
            'presence',
            {},
            xml('arpc', { xmlns: 'alfred:pb', ver: '1' })
        )
        await this.xmpp.send(arpcPresence)
        await this.observable.notify({
            key: 'online',
        })
        if (!this.pingServerInterval) {
            this.pingServerInterval = setInterval(async () => {
                await this.xmpp.send(arpcPresence)
            }, PING_SERVER_INTERVAL)
        }
    }

    offline = () => {
        clearInterval(this.pingServerInterval)
        this.pingServerInterval = null
    }

    status = (status) => {
        this.observable.notify({
            key: status,
        })
    }

    stanza = async (stanza) => {
        const { name } = stanza

        switch (name) {
            case 'message':
                this.handleMessage({ stanza })
                break
            case 'iq':
                this.handleIQ({ stanza })
                break
            case 'presence':
                this.handlePresence({ stanza })
                break
            default:
                // unknown type
                break
        }
    }

    onError = async (err) => {
        // stop reconnecting
        clearInterval(this.pingServerInterval)
        this.pingServerInterval = null
        if (
            /SASLError: not-authorized - Invalid username or password/.test(err)
        ) {
            const apiManager = APIManager.getInstance()
            await this.xmpp.stop()
            if (this.kvToken === apiManager.kvToken) {
                await apiManager.updateToken()
            }
            this.kvToken = apiManager.kvToken
            this.initXMPP()
            return this.xmpp.start()
        }
        if (/conflict - Replaced by new connection/.test(err)) {
            await this.xmpp.stop()
            return this.observable.notify({
                key: 'offline',
                data: 'conflict',
            })
        }
        if (/WebSocket ECONNERROR/.test(err)) {
            return window.logManager.log('WebSocket ECONNERROR')
        }

        if (this.times >= 9) {
            this.xmpp.reconnect.delay = retryIntervals[2]
        } else if (this.times >= 4) {
            this.xmpp.reconnect.delay = retryIntervals[1]
        }
        this.times++
        window.logManager.error(err)
    }

    initIqCallee = () => {
        const { iqCallee } = this.xmpp
        iqCallee.set('ivuu:setting', 'attrs', (ctx) => {
            return true
        })
        iqCallee.set('', 'ping', (ctx) => {
            return true
        })
        iqCallee.set('jabber:iq:roster', 'query', (ctx) => {
            return true
        })
    }

    async close(subscriber) {
        this.observable.unsubscribe(subscriber)
        return this.xmpp && (await this.xmpp.stop())
    }

    async connect() {
        if (!this.xmpp.socket || this.xmpp.socket.socket.readyState === 3) {
            await this.xmpp.start()
            window.eventManager.logXmppConnectedTime()
        }
        return
    }

    handleIQ({ stanza }) {
        const viewerJid = stanza.attrs.from
        const item = stanza.children[0].children[0]
        if (item && item.attrs && item.attrs.cache === 'trust:reset') {
            this.observable.notify({
                key: 'checkLiveViewerSharePermission',
            })
        }

        window.logManager.log(
            `[XMPP PB] IQ-${viewerJid}: ${JSON.stringify(stanza)}`
        )
    }

    async handlePresence({ stanza }) {
        if (stanza.attrs.from === this.xmppJid) {
            return
        }
        this.observable.notify({
            key: 'getPresence',
            data: stanza,
        })
    }

    handleMessage({ stanza }) {
        const viewerJid = stanza.attrs.from

        const ivuuMessages = stanza.getChild('ivuumessages')
        const webrtcMessage = stanza.getChild('webrtc')
        const arpc = stanza.getChild('arpc')

        // skip the message from camera itself
        if (viewerJid === this.deviceId) {
            return
        }

        if (arpc) {
            const b64str = arpc.text()
            const pbMsg = ProtoBuf.ProtoBufService.handleMessage(b64str)
            const rpcService = RpcServiceList[pbMsg.service]

            if (rpcService === undefined) {
                return
            }

            const reqId = pbMsg.id
            const type = pbMsg.type
            const control = pbMsg.control
            const payload = pbMsg.payload

            const message = rpcService.onMessage({ type, control, payload })
            const retdata = message.data

            window.logManager.log(
                `[XMPP PB] message-${reqId}: ${JSON.stringify(message)}`
            )

            if (
                type === ProtoBuf.MessageType.REQUEST &&
                isInstanceOf(rpcService, CameraSettings.CameraSettingsService)
            ) {
                switch (message.type) {
                    case 'getCameraSettings':
                        this.observable.notify({
                            key: 'getCameraSettings',
                            id: reqId,
                            viewerJid,
                        })
                        break
                    case 'setEnabled':
                        this.observable.notify({
                            key: 'setPreview',
                            id: reqId,
                            viewerJid,
                            enabled: retdata.enabled,
                        })
                        break
                    case 'setAlias':
                        this.observable.notify({
                            key: 'setAlias',
                            id: reqId,
                            viewerJid,
                            cameraAlias: retdata.alias,
                        })
                        break
                    case 'setLowLightFilter':
                        this.observable.notify({
                            key: 'setNight',
                            id: reqId,
                            viewerJid,
                            night: retdata.mode,
                        })
                        break
                    case 'setAccessPriority':
                        // TODO: ??
                        break
                    case 'setDetectionMode':
                        /* don't answer any motion setting
                        this.observable.notify({
                            key: 'setMotionStatus',
                            id: reqId,
                            viewerJid,
                            motionStatus: {
                                enabled: retdata.enabled,
                                detectionMode: retdata.mode,
                                sensitivity: retdata.sensitivity,
                            },
                        })
                        */
                        break
                    case 'setZoomInLock':
                        this.observable.notify({
                            key: 'setZoomInLock',
                            id: reqId,
                            viewerJid,
                            zoomInLock: retdata.enabled,
                        })
                        break
                    case 'setMicrophone':
                        this.observable.notify({
                            key: 'setMicrophone',
                            id: reqId,
                            viewerJid,
                            audio_enabled: retdata.enabled,
                        })
                        break
                    case 'setDetectionReminder':
                    case 'setDetectionSchedule':
                    case 'setDetectionZone':
                    case 'setPowerSaving':
                    case 'setStatusLed':
                    case 'setOSD':
                    case 'setContinuousRecording':
                    case 'setRotation':
                    case 'setLens':
                        this.observable.notify({
                            id: reqId,
                            key: 'unimplemented',
                            viewerJid,
                        })
                        break
                    default:
                        this.observable.notify({
                            id: reqId,
                            key: 'invalidArgument',
                            viewerJid,
                        })
                }
            } else if (
                type == ProtoBuf.MessageType.REQUEST &&
                isInstanceOf(rpcService, CameraInfo.CameraInfoService)
            ) {
                switch (message.type) {
                    case 'updateViewerCapability':
                        this.observable.notify({
                            key: 'updateViewerCapability',
                            id: reqId,
                            viewerJid,
                        })
                        break
                    case 'getCameraDeviceCapability':
                        this.observable.notify({
                            key: 'getCameraDeviceCapability',
                            id: reqId,
                            viewerJid,
                        })
                        break
                    case 'getSnapshot':
                        this.observable.notify({
                            key: 'getSnapshot',
                            id: reqId,
                            viewerJid,
                        })
                        break
                    case 'getCameraStatus':
                        this.observable.notify({
                            key: 'getCameraStatus',
                            id: reqId,
                            viewerJid,
                        })
                        break
                    case 'reloadConfig':
                        if (retdata.type === 1) {
                            // ConfigType.FEATURES
                            this.observable.notify({
                                id: reqId,
                                key: 'reloadFeature',
                                viewerJid,
                            })
                        } else {
                            this.sendOpResult({
                                viewerJid,
                                code: ProtoBuf.ErrorCode.UNIMPLEMENTED,
                                settingRevision:
                                    this.props.cameraContext.state
                                        .settingRevision,
                                message: '',
                                id: reqId,
                            })
                        }
                        break
                    case 'getCameraLog':
                        if (retdata.type === 0) {
                            // CameraLogType.SYSTEM_OVERVIEW
                            this.observable.notify({
                                id: reqId,
                                key: 'getCameraLog',
                                viewerJid,
                            })
                        } else {
                            this.sendOpResult({
                                viewerJid,
                                code: ProtoBuf.ErrorCode.UNIMPLEMENTED,
                                settingRevision:
                                    this.props.cameraContext.state
                                        .settingRevision,
                                message: '',
                                id: reqId,
                            })
                        }
                        break
                    case 'getCameraHealthStatus':
                        this.observable.notify({
                            id: reqId,
                            key: 'unimplemented',
                            viewerJid,
                        })
                        break
                    default:
                        this.observable.notify({
                            id: reqId,
                            key: 'invalidArgument',
                            viewerJid,
                        })
                }
            } else if (
                type == ProtoBuf.MessageType.REQUEST &&
                isInstanceOf(rpcService, CameraStatus.CameraStatusService)
            ) {
                switch (message.type) {
                    case 'liveSessionStatus':
                        this.observable.notify({
                            id: reqId,
                            key: 'getLiveSessionStatus',
                            viewerJid,
                            resolution: retdata.state,
                        })
                        break
                    case 'cameraRuntimeStatus':
                        this.observable.notify({
                            id: reqId,
                            key: 'getCameraRuntimeStatus',
                            viewerJid,
                            resolution: retdata.state,
                        })
                        break
                    default:
                        this.observable.notify({
                            id: reqId,
                            key: 'invalidArgument',
                            viewerJid,
                        })
                }
            } else if (
                type == ProtoBuf.MessageType.REQUEST &&
                isInstanceOf(rpcService, LiveControl.LiveControlService)
            ) {
                switch (message.type) {
                    case 'resolution':
                        this.observable.notify({
                            id: reqId,
                            key: 'setResolution',
                            viewerJid,
                            resolution: retdata.state,
                        })
                        break
                    case 'zoom':
                        this.observable.notify({
                            id: reqId,
                            key: 'zoom',
                            viewerJid,
                            zoom_in: retdata.state.zoomIn,
                            scale: retdata.state.scale,
                            coord_x: retdata.state.coordX,
                            coord_y: retdata.state.coordY,
                        })
                        break
                    case 'logUpload': // depurcated, replace by CameraLogResponse
                    case 'recordingResult':
                    case 'recordingControl':
                    case 'orientation':
                    case 'pushToTalk':
                    case 'torch':
                    case 'focus':
                    case 'siren':
                        this.observable.notify({
                            id: reqId,
                            key: 'unimplemented',
                            viewerJid,
                        })
                        break
                    default:
                        this.observable.notify({
                            id: reqId,
                            key: 'invalidArgument',
                            viewerJid,
                        })
                }
            }
        }

        if (webrtcMessage) {
            const message = webrtcMessage.children[0]

            try {
                JSON.parse(message)
            } catch (error) {
                return
            }
            const parsedMessage = JSON.parse(message)

            switch (parsedMessage.type) {
                case 'offer':
                    this.observable.notify({
                        key: 'getOffer',
                        viewerJid,
                        sdp: parsedMessage.sdp,
                    })
                    break
                case 'candidate':
                    this.observable.notify({
                        key: 'getRemoteCandidate',
                        viewerJid,
                        candidate: {
                            sdpMid: parsedMessage.sdpMid,
                            candidate: parsedMessage.candidate,
                        },
                    })
                    break
                case 'close':
                    this.observable.notify({
                        key: 'closeWebrtcConntection',
                        viewerJid,
                        reason: parsedMessage.reason,
                    })
                    break
                default:
                    break
            }
        }
    }

    async sendARPC({ to, binary }) {
        const lol = Buffer.from(binary).toString('base64')
        await this.xmpp.send(
            xml(
                'message',
                {
                    id: 'ivuu-viewer',
                    to,
                    xmlns: 'jabber:client',
                    from: this.xmppJid,
                },
                xml(
                    'arpc',
                    {
                        xmlns: 'alfred:pb',
                        from: this.xmppJid,
                    },
                    lol
                )
            )
        )
    }

    _unpackARPC(u8arr) {
        // util function for debug response message
        const b64str = Buffer.from(u8arr).toString('base64')
        const pbMsg = ProtoBuf.ProtoBufService.handleMessage(b64str)
        const rpcService = RpcServiceList[pbMsg.service]

        const reqId = pbMsg.id
        const type = pbMsg.type
        const control = pbMsg.control
        const payload = pbMsg.payload
        const message = rpcService.onMessage({ type, control, payload })
        console.warn(`[XMPP PB] send ${JSON.stringify(message)}`)
    }

    async sendOpResult({ to, code, resvion, message, id }) {
        const u8arr = ProtoBuf.ProtoBufService.operationResult(
            message,
            code,
            resvion,
            id
        )
        await this.sendARPC({ to, binary: u8arr })
    }

    async sendSnapshot({ image, to, id }) {
        const service = RpcServiceList[1]
        const u8arr = service.responseCameraSnapshot(id, image)
        await this.sendARPC({ to, binary: u8arr })
    }

    async sendLiveSessionStatus({ to, status, id }) {
        const service = RpcServiceList[3]

        const u8arr = service.respondLiveSessionStatus(id, status)
        await this.sendARPC({ to, binary: u8arr })
    }

    async sendCameraStatusResponse({ to, deviceInfoProtoObj, id }) {
        const cameraInfoService = RpcServiceList[1]
        const u8arr = cameraInfoService.responseCameraStatus(
            id,
            deviceInfoProtoObj
        )
        await this.sendARPC({ to, binary: u8arr })
    }

    async sendCameraStatus({ to, deviceInfoProtoObj, id }) {
        const cameraStatService = RpcServiceList[3]
        const u8arr = cameraStatService.sendCameraRuntimeStatus(
            id,
            deviceInfoProtoObj
        )
        await this.sendARPC({ to, binary: u8arr })
    }

    async sendCameraSetting({ to, settingProtoObj, id }) {
        const settingService = RpcServiceList[2]
        const u8arr = settingService.respondCameraSettings(id, settingProtoObj)

        this._unpackARPC(u8arr)

        await this.sendARPC({ to, binary: u8arr })
    }

    async sendCameraCapability({ to, id }) {
        const infoService = RpcServiceList[1]
        const u8arr = infoService.responseCameraDeviceCapability(id)
        await this.sendARPC({ to, binary: u8arr })
    }

    async sendCameraLogResponse({ to, id, jsonStr }) {
        const infoService = RpcServiceList[1]
        const u8arr = infoService.responseCameraLog(id, jsonStr)
        await this.sendARPC({ to, binary: u8arr })
    }

    async acceptConnection({ to, answer }) {
        await this.xmpp.send(
            xml(
                'message',
                {
                    id: 'ivuu-viewer',
                    to,
                    from: this.xmppJid,
                    xmlns: 'jabber:client',
                },
                xml(
                    'webrtc',
                    {
                        xmlns: 'alfred.camera',
                    },
                    answer
                )
            )
        )
    }

    async terminateViewerWebrtcConnection({ to, close, ssid, contentionPeer }) {
        const content = JSON.stringify({
            reason: close,
            type: 'close',
            id: ssid,
            contentionPeer,
        })
        await this.xmpp.send(
            xml(
                'message',
                {
                    id: 'ivuu-viewer',
                    to,
                    from: this.xmppJid,
                    xmlns: 'jabber:client',
                },
                xml(
                    'webrtc',
                    {
                        xmlns: 'alfred.camera',
                    },
                    content
                )
            )
        )
    }

    async sendIceCandidate({ to, candidate }) {
        await this.xmpp.send(
            xml(
                'message',
                {
                    id: 'ivuu-viewer',
                    to,
                    xmlns: 'jabber:client',
                },
                xml(
                    'webrtc',
                    {
                        xmlns: 'alfred.camera',
                    },
                    candidate
                )
            )
        )
    }
}
